import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCan,
  useCompanies,
  useDashboard,
  useResetDashboard,
  useSaveDashboard,
} from '../api/hooks';
import type { Dashboard, WidgetKey } from '../api/types';
import { BusinessPanel } from '../components/BusinessPanel';
import { DocRow, DocTable } from '../components/DocRow';
import { I } from '../components/Icons';
import { JobsPanel } from '../components/JobsPanel';
import { PayRow, PayTable } from '../components/PayRow';
import { Btn, Empty, H2, Surface, Tag } from '../components/ui';
import { useT } from '../i18n';
import { cap, MONTHS, MONTHS_L, money, money0, plural, todayIso } from '../lib/format';
import { useCompanyFilter } from '../state/company';
import { useTopbar } from '../state/topbar';
import { useUi } from '../state/ui';
import { HomeAll } from './HomeAll';

/**
 * Přehled = widgety z GET /api/dashboard podle role (kap. 39); uživatel si sadu upraví.
 * Na desktopu dva sloupce (.cols): vlevo čísla a grafy, vpravo „k vyřízení".
 */
export function Home() {
  const t = useT();
  useTopbar(t('nav.home'), { right: 'companyChip' });
  const navigate = useNavigate();
  const { activeCompanyId, isAll } = useCompanyFilter();
  const { data: companies = [] } = useCompanies();
  const companyId = isAll ? '' : (activeCompanyId ?? companies[0]?.id ?? '');
  const company = companies.find((c) => c.id === companyId);
  const cur = company?.currency ?? 'CZK';
  const { data: d } = useDashboard(companyId);
  const access = useCan(companyId);
  const { toast, openSheet, closeSheet } = useUi();
  const save = useSaveDashboard(companyId);
  const reset = useResetDashboard(companyId);
  const m = todayIso().slice(0, 7);
  const fmt0 = (v: string | number) => money0(v, cur);
  const fmt = (v: string | number) => money(v, cur);

  if (isAll) return <HomeAll />;

  const w = d?.data ?? {};
  const has = (k: WidgetKey) => !!d?.widgets.includes(k);
  const editWidgets = () =>
    openSheet(
      <WidgetChooser
        dashboard={d}
        onSave={(keys) =>
          save.mutate(keys, {
            onSuccess: () => {
              closeSheet();
              toast('Přehled uložen');
            },
          })
        }
        onReset={() =>
          reset.mutate(undefined, {
            onSuccess: () => {
              closeSheet();
              toast('Přehled podle role');
            },
          })
        }
        onCancel={closeSheet}
      />,
    );

  const rec = w.receivables;
  const openSum = Number(rec?.total ?? 0);
  const overSum = Number(rec?.overdueTotal ?? 0);
  const pct = openSum ? Math.round((overSum / openSum) * 100) : 0;
  const months = w.revenue?.months ?? [];
  const max = Math.max(...months.map((x) => Number(x.revenue)), 1);
  const suggestionCount = w.suggestions?.count ?? 0;
  const purchaseMissing = w.purchaseMissing?.count ?? 0;
  const todo =
    (w.overdue?.length ?? 0) + (w.unmatched?.length ?? 0) + suggestionCount + purchaseMissing;

  return (
    <div className="cols home-cols">
      <div className="home-main">
        {company && (!access.loaded || access.can('reports:read')) && (
          <div className="ho-biz">
            <BusinessPanel company={company} />
          </div>
        )}
        {has('revenue') && (
          <>
            <H2>
              {cap(MONTHS_L[Number(m.slice(5)) - 1] ?? '')} {m.slice(0, 4)}
            </H2>
            <Surface className="pad">
              <div className="two">
                <div>
                  <div className="k">Obrat bez DPH</div>
                  <div className="v">{fmt0(w.revenue?.month ?? 0)}</div>
                </div>
                <div>
                  <div className="k">Proti loňsku</div>
                  <div
                    className={`v ${Number(w.revenue?.previousYear.change ?? 0) < 0 ? 'bad' : 'ok'}`}
                  >
                    {w.revenue?.previousYear.change == null
                      ? '—'
                      : `${Number(w.revenue.previousYear.change) > 0 ? '+' : ''}${w.revenue.previousYear.change} %`}
                  </div>
                </div>
              </div>
              <svg
                className="chart"
                viewBox="0 0 300 108"
                role="img"
                aria-label="Obrat po měsících"
              >
                {months.map((mm, i) => {
                  const h = Math.max(2, (Number(mm.revenue) / max) * 78);
                  const x = i * 50 + 10;
                  return (
                    <g key={mm.month}>
                      <rect
                        x={x}
                        y={86 - h}
                        width="30"
                        height={h}
                        rx="4"
                        fill={i === months.length - 1 ? 'var(--brand)' : '#CFD2CB'}
                      />
                      <text
                        x={x + 15}
                        y="102"
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="600"
                        fill="#8A93A8"
                      >
                        {MONTHS[Number(mm.month.slice(5)) - 1]}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </Surface>
          </>
        )}

        {(has('margin') || has('cashflow') || has('costs') || has('vat')) && (
          <div className="widgets">
            {has('margin') && w.margin && (
              <Surface className="pad widget">
                <div className="k">Marže za měsíc</div>
                <div className="v">{fmt0(w.margin.margin)}</div>
                <div className="l2">
                  <span>
                    {w.margin.marginPct} % z {fmt0(w.margin.revenue)}
                  </span>
                  {w.margin.noCostItems > 0 && (
                    <span>{w.margin.noCostItems} položek bez nákladu</span>
                  )}
                </div>
              </Surface>
            )}
            {has('cashflow') && w.cashflow && (
              <Surface className="pad widget">
                <div className="k">Cash flow – výhled 30 dní</div>
                <div className={`v ${Number(w.cashflow.outlook.expected30) < 0 ? 'bad' : 'ok'}`}>
                  {fmt0(w.cashflow.outlook.expected30)}
                </div>
                <div className="l2">
                  <span>přijde {fmt0(w.cashflow.outlook.receivablesDue30)}</span>
                  <span>odejde {fmt0(w.cashflow.outlook.payablesDue30)}</span>
                </div>
              </Surface>
            )}
            {has('costs') && w.costs && (
              <Surface className="pad widget">
                <div className="k">Náklady za měsíc</div>
                <div className="v">{fmt0(w.costs.total)}</div>
                <div className="l2">
                  <span>{w.costs.documents} přijatých faktur</span>
                </div>
              </Surface>
            )}
            {has('vat') && (
              <Surface className="pad widget">
                <div className="k">DPH {w.vat?.label ?? ''}</div>
                <div className="v">
                  {w.vat
                    ? Number(w.vat.refund) > 0
                      ? `−${fmt0(w.vat.refund)}`
                      : fmt0(w.vat.due)
                    : '—'}
                </div>
                <div className="l2">
                  {w.vat && <span>výstup {fmt0(w.vat.output)}</span>}
                  {w.vat && <span>odpočet {fmt0(w.vat.deduction)}</span>}
                </div>
              </Surface>
            )}
          </div>
        )}

        {has('stock') && w.stock && (
          <>
            <H2>{t('home.stock')}</H2>
            <Surface className="pad">
              <div className="two">
                <div>
                  <div className="k">Hodnota skladu</div>
                  <div className="v">{fmt0(w.stock.totalValue)}</div>
                </div>
                <div>
                  <div className="k">Kusů skladem</div>
                  <div className="v">{w.stock.unitsCount}</div>
                </div>
              </div>
              <div className="l2 mt">
                <span>
                  prodáno {w.stock.sold.count} ks · marže {fmt0(w.stock.sold.margin)}
                </span>
                {w.stock.belowMin > 0 && (
                  <span className="warn">{w.stock.belowMin} pod minimem</span>
                )}
              </div>
            </Surface>
          </>
        )}

        {(has('top_customers') || has('top_products')) && (
          <div className="widgets">
            {has('top_customers') && (
              <Surface className="widget">
                <div className="k pad-h">Nejlepší zákazníci</div>
                {(w.top_customers ?? []).map((c) => (
                  <button
                    type="button"
                    className="row"
                    key={c.contactId}
                    onClick={() => navigate(`/vice/kontakty/${c.contactId}`)}
                  >
                    <div className="grow">
                      <div className="l1">{c.name}</div>
                      <div className="l2">{c.documents} dokl.</div>
                    </div>
                    <div className="amt">{fmt0(c.revenue)}</div>
                  </button>
                ))}
                {!w.top_customers?.length && <Empty>Zatím nic.</Empty>}
              </Surface>
            )}
            {has('top_products') && (
              <Surface className="widget">
                <div className="k pad-h">Nejprodávanější produkty</div>
                {(w.top_products ?? []).map((p) => (
                  <div className="row" key={p.productId}>
                    <div className="grow">
                      <div className="l1">{p.name}</div>
                      <div className="l2">{p.quantity} ks</div>
                    </div>
                    <div className="amt">{fmt0(p.revenue)}</div>
                  </div>
                ))}
                {!w.top_products?.length && <Empty>Zatím nic.</Empty>}
              </Surface>
            )}
          </div>
        )}
        {has('orders') && w.orders && (
          <Surface className="pad mt">
            <div className="k">Objednávky přijaté</div>
            <div className="v">
              {w.orders.open} otevřených · {w.orders.confirmed} potvrzených
            </div>
            <div className="l2">{fmt(w.orders.total)}</div>
          </Surface>
        )}
      </div>
      <div className="home-side">
        {has('receivables') && (
          <section className="hero ho-hero">
            <div className="hero-l">Čeká na zaplacení</div>
            <div className="hero-n">{fmt0(openSum)}</div>
            <div className="bar">
              <span style={{ width: `${100 - pct}%` }} />
              <span className="bad" style={{ width: `${pct}%` }} />
            </div>
            <div className="hero-row">
              <span>
                <i className="dot ok" />v termínu {fmt0(openSum - overSum)}
              </span>
              <span>
                <i className="dot bad" />
                po splatnosti {fmt0(overSum)} ({rec?.overdueCount ?? 0})
              </span>
            </div>
            {rec && (Number(rec.dueWeek) > 0 || rec.overpaidCount > 0) && (
              <div className="hero-row">
                {Number(rec.dueWeek) > 0 && <span>splatné do týdne {fmt0(rec.dueWeek)}</span>}
                {rec.overpaidCount > 0 && (
                  <span>
                    přeplatky {fmt0(rec.overpaidTotal)} ({rec.overpaidCount})
                  </span>
                )}
              </div>
            )}
          </section>
        )}
        <div className="quick ho-quick">
          <Btn variant="primary" onClick={() => navigate('/novy')}>
            {I.plus}
            {t('home.newInvoice')}
          </Btn>
          <Btn onClick={() => navigate('/doklady/prijata/nova')}>Nahrát přijatou fakturu</Btn>
          {(!access.loaded || access.can('jobs:write')) && (
            <Btn onClick={() => navigate('/zakazky/nova')}>Nová zakázka</Btn>
          )}
          <Btn onClick={() => navigate('/velkoobchod')}>{t('nav.wholesale')}</Btn>
          <Btn onClick={() => navigate('/platby')}>{t('nav.payments')}</Btn>
          <Btn onClick={() => navigate('/prehledy')}>{t('nav.reports')}</Btn>
          <Btn onClick={() => navigate('/sklad/skener')}>{t('stock.scanner')}</Btn>
        </div>

        {companyId && (!access.loaded || access.can('jobs:read')) && (
          <JobsPanel companyId={companyId} canWrite={!access.loaded || access.can('jobs:write')} />
        )}
        {(has('overdue') || has('unmatched')) && (
          <>
            <H2>
              {t('home.todo')} {todo ? <span className="cnt">{todo}</span> : null}
            </H2>
            {w.overdue?.length ? (
              <DocTable>
                {w.overdue.map((doc) => (
                  <DocRow key={doc.id} doc={doc} />
                ))}
              </DocTable>
            ) : null}
            {suggestionCount > 0 && (
              <Surface className={`pad ${w.overdue?.length ? 'mt' : ''}`.trim()}>
                <div className="row" data-testid="todo-suggestions">
                  <div className="grow">
                    <div className="l1">
                      {suggestionCount}{' '}
                      {plural(suggestionCount, [
                        'návrh k potvrzení',
                        'návrhy k potvrzení',
                        'návrhů k potvrzení',
                      ])}
                    </div>
                    <div className="l2">
                      Platby z výpisu bez VS – zkontroluj a spáruj v Platbách.
                    </div>
                  </div>
                  <Btn sm onClick={() => navigate('/platby')}>
                    Otevřít
                  </Btn>
                </div>
              </Surface>
            )}
            {purchaseMissing > 0 && (
              <Surface className={`pad ${w.overdue?.length || suggestionCount ? 'mt' : ''}`.trim()}>
                <div className="row" data-testid="todo-purchase-missing">
                  <span className="purchase-miss" aria-hidden="true">
                    !
                  </span>
                  <div className="grow">
                    <div className="l1">
                      {purchaseMissing}{' '}
                      {plural(purchaseMissing, [
                        'faktura bez nákupního dokladu',
                        'faktury bez nákupního dokladu',
                        'faktur bez nákupního dokladu',
                      ])}
                    </div>
                    <div className="l2">
                      Chybí přijatá faktura od dodavatele nebo výkupní list k prodanému kusu.
                    </div>
                  </div>
                  <Btn sm onClick={() => navigate('/doklady?nakup=chybi')}>
                    Zobrazit
                  </Btn>
                </div>
              </Surface>
            )}
            {w.unmatched?.length ? (
              <PayTable
                className={w.overdue?.length || suggestionCount || purchaseMissing ? 'mt' : ''}
              >
                {w.unmatched.map((p) => (
                  <PayRow
                    key={p.id}
                    payment={p}
                    onMatch={() => {
                      navigate('/platby');
                      toast('Spáruj platbu v Platbách');
                    }}
                  />
                ))}
              </PayTable>
            ) : null}
            {!todo && (
              <Surface>
                <Empty>Vše je vyřízené. Žádné doklady po splatnosti ani nespárované platby.</Empty>
              </Surface>
            )}
          </>
        )}
        {has('payables') && w.payables && (
          <>
            <H2>{t('home.payables')}</H2>
            <Surface className="pad">
              <div className="two">
                <div>
                  <div className="k">K zaplacení</div>
                  <div className="v">{fmt0(w.payables.total)}</div>
                </div>
                <div>
                  <div className="k">Po splatnosti</div>
                  <div className={`v ${Number(w.payables.overdueTotal) > 0 ? 'bad' : ''}`}>
                    {fmt0(w.payables.overdueTotal)} ({w.payables.overdueCount})
                  </div>
                </div>
              </div>
            </Surface>
            {w.payables.list.length > 0 && (
              <Surface className="mt">
                {w.payables.list.map((p) => (
                  <button
                    type="button"
                    className="row"
                    key={p.id}
                    onClick={() => navigate(`/doklady/${p.id}`)}
                  >
                    <div className="grow">
                      <div className="l1">{p.customerName}</div>
                      <div className="l2">
                        <span>{p.number}</span>
                        <span>splatnost {p.dueAt}</span>
                      </div>
                    </div>
                    <div className="amt">{money(p.remaining, p.currency)}</div>
                  </button>
                ))}
              </Surface>
            )}
          </>
        )}
        <div className="actions mt">
          <Btn sm onClick={editWidgets}>
            Upravit přehled
          </Btn>
          {d?.custom && <Tag>vlastní výběr</Tag>}
        </div>
      </div>
    </div>
  );
}

function WidgetChooser({
  dashboard,
  onSave,
  onReset,
  onCancel,
}: {
  dashboard: Dashboard | undefined;
  onSave: (keys: WidgetKey[]) => void;
  onReset: () => void;
  onCancel: () => void;
}) {
  const [keys, setKeys] = useState<WidgetKey[]>(dashboard?.widgets ?? []);
  const toggle = (k: WidgetKey) =>
    setKeys((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  return (
    <div>
      <h3>Co chceš v přehledu</h3>
      <div className="muted mb">
        Výchozí sada je podle role ({dashboard?.role ?? '…'}); vyber si vlastní.
      </div>
      {(dashboard?.available ?? []).map((wg) => (
        <label key={wg.key} className="check-row">
          <input type="checkbox" checked={keys.includes(wg.key)} onChange={() => toggle(wg.key)} />
          <span>{wg.label}</span>
        </label>
      ))}
      <Btn variant="primary" full onClick={() => onSave(keys)}>
        Uložit
      </Btn>
      <Btn full onClick={onReset}>
        Vrátit výchozí podle role
      </Btn>
      <Btn full onClick={onCancel}>
        Zrušit
      </Btn>
    </div>
  );
}
